import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { ApiError, type SignInInput } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { PasswordInput } from "../components/PasswordInput";

/**
 * Run a sign-in attempt and normalise its failure for the form.
 *
 * Success is the only path that lets AuthProvider set a user; a rejected
 * sign-in returns here and the page stays mounted, so there is no route into
 * My Desk.
 */
export async function attemptSignIn(
  signIn: (input: SignInInput) => Promise<void>,
  input: SignInInput,
): Promise<ApiError | null> {
  try {
    await signIn(input);
    return null;
  } catch (caught) {
    return caught instanceof ApiError
      ? caught
      : new ApiError({ code: "network", message: "Could not reach the server.", status: 0 });
  }
}

export function SignInPage() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      setError(await attemptSignIn(signIn, { email, password }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      {/* The pitch panel: what the product is, in three lines. */}
      <div className="hidden flex-col justify-between bg-slate-900 p-10 text-white lg:flex">
        <div className="flex items-center gap-2.5">
          <div className="flex size-8 items-center justify-center rounded-md bg-white text-sm font-semibold text-slate-900">
            S
          </div>
          <span className="text-sm font-semibold">SBM One</span>
        </div>

        <div className="max-w-sm">
          <h1 className="text-2xl font-semibold leading-tight tracking-tight">
            Customers, people and billing. One system.
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-300">
            Built for salons, clinics and agencies. Your team, your calendar and
            your money in one place, with the records joined properly.
          </p>
        </div>

        <ul className="space-y-3 text-sm text-slate-300">
          {[
            ["Customers", "Contacts, leads, deals and a timeline for each."],
            ["People", "Roster, attendance, leave and payroll."],
            ["Billing", "Invoices, payments and what is owed."],
          ].map(([title, body]) => (
            <li key={title}>
              <span className="font-medium text-white">{title}</span> — {body}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="flex size-8 items-center justify-center rounded-md bg-slate-900 text-sm font-semibold text-white">
              S
            </div>
            <span className="text-sm font-semibold text-slate-900">SBM One</span>
          </div>

          <h2 className="text-xl font-semibold tracking-tight text-slate-900">Sign in</h2>
          <p className="mt-1 text-sm text-slate-500">Use your work email address.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <Field
              id="email"
              label="Email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={setEmail}
              errors={error?.fields.email}
            />
            <Field
              id="password"
              label="Password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={setPassword}
              errors={error?.fields.password ?? error?.fields.detail}
            />

            {error && !error.fields.email && !error.fields.password && !error.fields.detail ? (
              <p role="alert" className="text-sm text-rose-600">
                {error.message}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-slate-900 px-3 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-500">
            New to SBM One?{" "}
            <Link to="/signup" className="font-medium text-slate-900 hover:underline">
              Create an account
            </Link>
          </p>

          <p className="mt-6 rounded-md bg-slate-100 px-3 py-2.5 text-xs text-slate-600">
            Demo account: <span className="font-medium">demo@sbmone.test</span> /{" "}
            <span className="font-medium">DemoPass1234</span>
          </p>
        </div>
      </div>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  type: string;
  autoComplete: string;
  required?: boolean | undefined;
  value: string;
  onChange: (value: string) => void;
  errors?: string[] | undefined;
}

function Field({
  id,
  label,
  type,
  autoComplete,
  required,
  value,
  onChange,
  errors,
}: FieldProps) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      {type === "password" ? (
        <PasswordInput
          id={id}
          autoComplete={autoComplete}
          required={required}
          value={value}
          onChange={onChange}
          aria-invalid={errors?.length ? true : undefined}
          aria-describedby={errors?.length ? `${id}-error` : undefined}
          className="mt-1"
        />
      ) : (
        <input
          id={id}
          name={id}
          type={type}
          autoComplete={autoComplete}
          required={required}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={errors?.length ? true : undefined}
          aria-describedby={errors?.length ? `${id}-error` : undefined}
          className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm text-slate-900 ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
        />
      )}
      {errors?.length ? (
        <p id={`${id}-error`} role="alert" className="mt-1 text-sm text-rose-600">
          {errors.join(" ")}
        </p>
      ) : null}
    </div>
  );
}